import React from 'react';
import { cn } from '../../design-system/cn';
import { semanticColors, domainColors, radii, shadows, motion } from '../../design-system/tokens';
import { glassSurfaces } from '../../design-system/tokens/surfaces';

const VARIANTS = {
  primary:
    `${semanticColors.accent.bg} ${semanticColors.accent.bgHover} ${semanticColors.accent.onAccent} ${shadows.brandMd}`,
  secondary:
    `bg-gray-800 hover:bg-gray-700 text-gray-200 border ${semanticColors.border.strong}`,
  ghost:
    `${semanticColors.surface.glass} hover:bg-white/10 text-white ${semanticColors.border.subtle}`,
  glass:
    `${glassSurfaces.elevated} hover:bg-white/[0.08] text-white border ${semanticColors.border.subtle} ${shadows.premiumGlass}`,
  danger:
    'bg-red-600 hover:bg-red-500 text-white',
  destructive:
    'bg-red-500 hover:bg-red-400 text-black',
  admin:
    `${domainColors.admin.accent} hover:bg-violet-400 text-black`,
  warning:
    'bg-amber-500 hover:bg-amber-400 text-black',
  link:
    `bg-transparent ${semanticColors.accent.text} hover:underline p-0 min-h-0 font-bold normal-case tracking-normal`,
  close:
    `bg-gray-800 border ${semanticColors.border.strong} text-gray-400 hover:text-white min-h-0 p-0 w-10 h-10 ${radii.controlMd}`,
  wallet:
    `${domainColors.wallet.surface} border ${domainColors.wallet.border} ${domainColors.wallet.text} hover:bg-purple-500/25`,
  headerIcon:
    `min-h-0 w-10 h-10 p-0 ${radii.controlMd} ${semanticColors.background.raised} border ${semanticColors.border.DEFAULT} text-gray-300 hover:text-white hover:border-emerald-500/40`,
  profileIcon:
    `min-h-0 w-10 h-10 p-0 ${radii.controlMd} bg-gray-800 border ${semanticColors.border.strong} text-gray-300 hover:text-white hover:border-emerald-500/50 overflow-hidden`,
};

const SIZES = {
  sm: `px-4 py-2 text-[10px] tracking-widest ${radii.control}`,
  md: `px-6 py-3 text-sm tracking-widest ${radii.controlMd}`,
  lg: `px-10 py-4 text-sm tracking-[0.2em] ${radii.controlMd}`,
  pill: `px-6 py-3 text-[11px] tracking-wider ${radii.pill}`,
  icon: 'p-0 min-h-0',
  header: `px-6 py-2 text-xs tracking-widest ${radii.controlMd} min-h-0`,
};

function Spinner() {
  return (
    <svg
      className="animate-spin h-4 w-4 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.4 0 0 5.4 0 12h4z" />
    </svg>
  );
}

export default function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  loadingLabel,
  className,
  type = 'button',
  disabled,
  children,
  ...props
}) {
  const isLink = variant === 'link';
  const isClose = variant === 'close';
  const isIconVariant = variant === 'headerIcon' || variant === 'profileIcon';

  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center gap-2 font-black uppercase',
        motion.transition,
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0',
        !isLink && !isClose && !isIconVariant && motion.activeScale,
        VARIANTS[variant] || VARIANTS.primary,
        !isLink && !isClose && !isIconVariant && (SIZES[size] || SIZES.md),
        fullWidth && 'w-full',
        className
      )}
      {...props}
    >
      {loading && <Spinner />}
      {loading && loadingLabel ? loadingLabel : children}
    </button>
  );
}
